import { parseCalendarDate } from './calendar-date';

export interface SaleFinancialSummary {
  collected: number;
  refunded: number;
  net_collected: number;
  amount_due: number | null;
  return_review: boolean;
}
export interface SaleShareSettings {
  enabled: boolean;
  business_name: string;
  default_country: 'IN' | 'international';
  closing_text: string;
  include_address: boolean;
  include_seller: boolean;
}
export interface SaleSharePayload {
  id: string;
  updated_at: string;
  sale_number: string;
  sale_date: string;
  customer_name: string | null;
  customer_mobile: string | null;
  customer_address: string | null;
  contact_source: 'sale' | 'customer' | 'missing';
  seller_name: string | null;
  sale_status: string;
  payment_status: string;
  return_status: string | null;
  total_amount: number;
  delivery_charge: number;
  financial_summary: SaleFinancialSummary;
  settings: SaleShareSettings;
  items: Array<{
    name: string;
    size: string | null;
    color: string | null;
    quantity: number;
    returned_qty: number;
    unit_price: number;
    discount: number;
    line_total: number;
  }>;
}
export interface SalesPersonSummary {
  person_key: string;
  name: string;
  active: boolean;
  order_count: number;
  invoice_value: number;
  collected: number;
  refunded: number;
  net_collected: number;
  amount_due: number;
  return_review_count: number;
  delivered_count: number;
  returned_count: number;
  returned_units: number;
  draft_count: number;
  cancelled_count: number;
  share_percent: number | null;
}
export interface SalesContribution {
  people: SalesPersonSummary[];
  total_people: number;
  totals: Omit<SalesPersonSummary, 'person_key' | 'name' | 'active' | 'share_percent'>;
  from: string;
  to: string;
  timezone: string;
  as_of: string;
  mode: 'seller' | 'created';
}

export function saleMoney(value: number | string | null | undefined): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));
}
export function saleStatusLabel(value: string | null | undefined): string {
  return value
    ? value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Not recorded';
}

const whatsappStatusIcons: Readonly<Record<string, string>> = {
  // Keep generated icons to single BMP characters, including in source encodings.
  draft: '\u270F',
  booked: '\u2611',
  confirmed: '\u2705',
  pending: '\u23F3',
  delivery_pending: '\u23F3',
  delivered: '\u2705',
  cancelled: '\u274C',
  partially_returned: '\u21A9',
  returned: '\u21A9',
  unpaid: '\u23F3',
  partially_paid: '\u23F3',
  paid: '\u2705',
  partially_refunded: '\u21A9',
  refunded: '\u21A9',
  none: '\u2796',
  requested: '\u23F3',
  accepted: '\u2705',
  rejected: '\u274C',
  completed: '\u2705',
};

function saleWhatsAppStatusLabel(value: string | null | undefined): string {
  const label = value === 'unpaid' ? 'Pending' : saleStatusLabel(value);
  const icon =
    value && Object.hasOwn(whatsappStatusIcons, value) ? whatsappStatusIcons[value] : undefined;
  return icon ? `${label} ${icon}` : label;
}

const whatsappDivider = '\u2500'.repeat(22);
const whatsappItemDivider = '-'.repeat(40);

export function saleDateLabel(value: string): string {
  return (
    parseCalendarDate(value)?.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) ?? value
  );
}
/** Formatting validation, never evidence that a recipient has a WhatsApp account. */
export function whatsappRecipient(
  raw: string,
  country: 'IN' | 'international',
): { digits?: string; error?: string } {
  const text = raw.trim();
  if (!text) return { error: 'Add a customer mobile number to this sale first.' };
  if (!/^[+\d\s().-]+$/.test(text))
    return { error: 'Enter one mobile number without letters or an extension.' };
  let value = text.replace(/[\s().-]/g, '');
  if (value.startsWith('00')) value = `+${value.slice(2)}`;
  if (country === 'IN' && !value.startsWith('+')) {
    if (/^0[6-9]\d{9}$/.test(value)) value = value.slice(1);
    if (/^[6-9]\d{9}$/.test(value)) value = `+91${value}`;
    else if (/^91[6-9]\d{9}$/.test(value)) value = `+${value}`;
    else return { error: 'Use a 10-digit Indian mobile number or an explicit +country code.' };
  }
  if (!/^\+[1-9]\d{6,14}$/.test(value))
    return { error: 'Use international format, including + and the country code (7–15 digits).' };
  if (value.startsWith('+91') && !/^\+91[6-9]\d{9}$/.test(value))
    return { error: 'An Indian mobile number must have 10 digits after +91.' };
  return { digits: value.slice(1) };
}
export function saleWhatsAppMessage(
  payload: SaleSharePayload,
  options: { address: boolean; compact?: boolean },
): string {
  const totals = payload.items.reduce(
    (summary, item) => ({
      quantity: summary.quantity + item.quantity,
      subtotal: summary.subtotal + item.unit_price * item.quantity,
      discount: summary.discount + item.discount,
    }),
    { quantity: 0, subtotal: 0, discount: 0 },
  );
  const businessName = payload.settings.business_name.replace(/[A-Za-z]/g, (letter) => {
    const uppercase = letter === letter.toUpperCase();
    const alphabetStart = uppercase ? 'A' : 'a';
    const boldAlphabetStart = uppercase ? 0x1d400 : 0x1d41a;
    return String.fromCodePoint(boldAlphabetStart + letter.charCodeAt(0) - alphabetStart.charCodeAt(0));
  });
  const lines = [
    `*\u2728${businessName}\u{1F90D}*`,
    `Order *${payload.sale_number}* | ${saleDateLabel(payload.sale_date)}`,
    whatsappDivider,
    '',
    payload.customer_name ? `Hi ${payload.customer_name},` : 'Hello,',
    '',
    '*\u{1F6D2} Product Summary*',
    whatsappItemDivider,
  ];
  if (!options.compact)
    payload.items.forEach((item, index) => {
      lines.push(`\u{1F518} ${index + 1}. *${item.name}*`);
      const variant = [item.size, item.color].filter(Boolean).join(' | ');
      if (variant) {
        const variantLabel = item.size && item.color ? 'Size & Color' : item.size ? 'Size' : 'Color';
        lines.push(
          `\u{1F3F3}\uFE0F\u200D\u{1F308} ${variantLabel}: *${variant}*`,
        );
      }
      lines.push(
        '',
        `Qty: ${item.quantity} x ${saleMoney(item.unit_price)} = ${saleMoney(item.quantity * item.unit_price)}`,
        `\u2796 Discount: ${saleMoney(item.discount)}`,
        `\u2611\uFE0F *Item total: ${saleMoney(item.line_total)}*`,
      );
      if (item.returned_qty > 0) lines.push(`Returned: ${item.returned_qty}`);
      lines.push(whatsappDivider, '');
    });
  else
    lines.push(`Qty: ${totals.quantity} | Item details available on request.`, whatsappDivider, '');
  lines.push(
    '\u2630 *Bill summary*',
    `_*Subtotal: ${saleMoney(totals.subtotal)}*_`,
    `\u2796 Total discount: ${saleMoney(totals.discount)}`,
  );
  if (payload.delivery_charge !== 0) lines.push(`Delivery: ${saleMoney(payload.delivery_charge)}`);
  lines.push(`\u2611\uFE0F *Total: ${saleMoney(payload.total_amount)}*`);
  const financial = payload.financial_summary;
  if (financial.refunded > 0) {
    lines.push(
      `Received: ${saleMoney(financial.collected)}`,
      `Refunded: ${saleMoney(financial.refunded)}`,
      `Net paid: ${saleMoney(financial.net_collected)}`,
    );
  } else lines.push(`Paid: ${saleMoney(financial.net_collected)}`);
  if (financial.return_review)
    lines.push('_Return under review. Contact us for the adjusted balance._');
  else if (financial.amount_due !== null) {
    lines.push(`*Due: ${saleMoney(financial.amount_due)}*`);
  }
  lines.push(
    whatsappDivider,
    '*\u{1F4E8} Order Status*',
    `Order: *${saleWhatsAppStatusLabel(payload.sale_status)}*`,
    `Payment: *${saleWhatsAppStatusLabel(payload.payment_status)}*`,
  );
  if (payload.return_status && payload.return_status !== 'none')
    lines.push(`Return: *${saleWhatsAppStatusLabel(payload.return_status)}*`);
  lines.push(whatsappDivider);
  if (options.address && payload.customer_address)
    lines.push('', '\u{1F4CD} *Delivery to*', payload.customer_address, whatsappDivider);
  if (payload.settings.include_seller && payload.seller_name)
    lines.push('', `\u270E Seller: ${payload.seller_name}`, whatsappDivider);
  if (payload.settings.closing_text.trim())
    lines.push('', `\u2764 ${payload.settings.closing_text.trim()}`);
  return lines.join('\n');
}
export function saleWhatsAppUrl(
  digits: string,
  message: string,
  target: 'universal' | 'web' = 'universal',
): string {
  if (!/^[1-9]\d{6,14}$/.test(digits)) throw new Error('Check the customer mobile number.');
  // Desktop can bypass the universal-link/native-app handoff that can corrupt emoji.
  if (target === 'web')
    return `https://web.whatsapp.com/send?phone=${digits}&text=${encodeURIComponent(message)}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}
export function salesBusinessDate(timezone = 'Asia/Kolkata', now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}-${parts.find((p) => p.type === 'day')!.value}`;
}
export function salesDateRange(
  period: 'today' | 'week' | '7d' | '30d' | '6m' | '1y' | 'month',
  timezone = 'Asia/Kolkata',
) {
  const to = salesBusinessDate(timezone);
  const date = parseCalendarDate(to)!;
  if (period === 'today') {
    /* Already the business date. */
  } else if (period === 'week') date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  else if (period === '7d' || period === '30d')
    date.setDate(date.getDate() - (period === '7d' ? 6 : 29));
  else {
    date.setDate(1);
    if (period !== 'month') date.setMonth(date.getMonth() - (period === '6m' ? 5 : 11));
  }
  const from = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return { from, to };
}
